import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProductOrderBody, ProductView, ProductViewWithLogEvents, SettingSetView } from '@lemoncloud/codes-goods-api';
import { Params, createAsyncDelay } from '@lemoncloud/lemon-web-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { createQueryKeys } from '@codes/base-api';
import { useCustomMutation } from '@codes/shared';

import {
    approveProduct,
    autoDeployProduct,
    cancelProduct,
    createProduct,
    deleteProduct,
    fetchChildProducts,
    fetchProductById,
    fetchProductSettingsById,
    fetchProducts,
    fetchProductsByProjectId,
    orderCatalogs,
    updateProduct,
    updateProductAtAdmin,
    updateProductSettings,
} from '../apis';
import { CREATE_PRODUCT_KEY, ORDER_CATALOGS_KEY, UPDATE_PRODUCT_AT_ADMIN_KEY, UPDATE_PRODUCT_KEY } from '../consts';
import {
    ApproveProductDTO,
    AutoDeployProductDTO,
    CreateProductBody,
    HandleErrorEventFunc,
    HandleSuccessEventFunc,
    PaginationType,
    UpdateProductBody,
    UpdateProductSettingsDTO,
} from '../types';
import {
    getDeploymentTrackingData,
    getSavedProductStatus,
    startDeploymentTracking,
    stopDeploymentTracking,
    updateDeploymentStatus,
} from '../utils/deploymentTracking';

export const productSettingsKey = createQueryKeys('product-settings');

export const useOrderCatalogs = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [ORDER_CATALOGS_KEY],
        mutationFn: async (body: ProductOrderBody) => {
            const response = await orderCatalogs(body);
            await createAsyncDelay(1000);
            return response;
        },
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useCreateProduct = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [CREATE_PRODUCT_KEY],
        mutationFn: async (body: CreateProductBody) => await createProduct(body),
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useUpdateProduct = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [UPDATE_PRODUCT_KEY],
        mutationFn: async (body: UpdateProductBody) => await updateProduct(body),
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useUpdateProductAtAdmin = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [UPDATE_PRODUCT_AT_ADMIN_KEY],
        mutationFn: async ({
            updateProductBody,
            autoDeployProductDTO,
        }: {
            updateProductBody: UpdateProductBody;
            autoDeployProductDTO?: AutoDeployProductDTO;
        }) => {
            const isAssigned = updateProductBody.body?.state === 'assigned';

            if (isAssigned && autoDeployProductDTO) {
                await autoDeployProduct(autoDeployProductDTO);
            }

            const response = await updateProductAtAdmin(updateProductBody);

            return response;
        },
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const productsKey = {
    ...createQueryKeys('products'),
    children: (parentId?: string) => ['products', 'children', parentId] as const,
};

const isProductAlreadyProcessed = (project: string, productId: string): boolean => {
    const storageKey = `frontProductDeploy_${project}_${productId}`;
    try {
        return localStorage.getItem(storageKey) === 'true';
    } catch {
        return true;
    }
};

const markProductAsProcessed = (project: string, productId: string): boolean => {
    const storageKey = `frontProductDeploy_${project}_${productId}`;
    try {
        localStorage.setItem(storageKey, 'true');
        return true;
    } catch {
        return false;
    }
};

const hasAllReferredSettingsCompleted = (settings: SettingSetView[]): boolean => {
    const referredSettings = settings.filter(setting => setting.refers);
    return (
        referredSettings.length > 0 &&
        referredSettings.every(setting => setting.value !== null && setting.value !== undefined)
    );
};

export const useFetchProductsByProjectId = (project: string, params: Params = {}) =>
    useQuery<PaginationType<ProductView[]>>({
        queryKey: productsKey.list({ projectId: project, ...params }),
        queryFn: async () => {
            const result = await fetchProductsByProjectId(project, params);
            return { ...result, data: result.list || [] } as PaginationType<ProductView[]>;
        },
        refetchOnWindowFocus: false,
        enabled: !!project,
        refetchOnMount: true,
        staleTime: 0,
        refetchInterval: 10000,
    });

// Polls products with toast notifications and auto-deployment for front products
export const useFetchProductsByProjectIdWithToast = (
    project: string,
    params: Params = {},
    enableToastAndDeploy: boolean = true
) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const autoDeployMutation = useAutoDeployProduct();
    const [previousProductsStatus, setPreviousProductsStatus] = useState<Record<string, string>>({});
    const isProcessingRunning = useRef(false);

    const query = useQuery<PaginationType<ProductView[]>>({
        queryKey: productsKey.list({ projectId: project, ...params }),
        queryFn: async () => {
            const result = await fetchProductsByProjectId(project, params);
            return { ...result, data: result.list || [] } as PaginationType<ProductView[]>;
        },
        refetchOnWindowFocus: false,
        enabled: !!project,
        refetchOnMount: true,
        staleTime: 0,
        refetchInterval: 10000,
    });

    const processProducts = useCallback(
        async (products: ProductView[]) => {
            for (const product of products) {
                try {
                    if (!product.id || isProductAlreadyProcessed(project, product.id)) {
                        continue;
                    }

                    const productSettings = await fetchProductSettingsById(product.id);

                    if (!hasAllReferredSettingsCompleted(productSettings)) {
                        continue;
                    }

                    if (!markProductAsProcessed(project, product.id)) {
                        continue;
                    }

                    toast({
                        title: t('product.frontend.configComplete.title'),
                        description: t('product.frontend.configComplete.singleProduct', { productName: product.name }),
                    });

                    try {
                        startDeploymentTracking(product.id, product.status || '');

                        await autoDeployMutation.mutateAsync(product.id);

                        queryClient.setQueryData(
                            productsKey.list({ projectId: project, ...params }),
                            (oldData: PaginationType<ProductView[]> | undefined) => {
                                if (!oldData) return oldData;

                                const updateProduct = (p: ProductView): ProductView =>
                                    p.id === product.id ? { ...p, status: 'busy', progress$: undefined } : p;

                                return {
                                    ...oldData,
                                    list: oldData.list?.map(updateProduct),
                                    data: oldData.data?.map(updateProduct),
                                };
                            }
                        );

                        toast({
                            title: t('product.frontend.deploy.requestSuccess'),
                            description: t('product.frontend.deploy.requestSuccessDescription', {
                                productName: product.name,
                            }),
                        });
                    } catch (error) {
                        console.error(`Deployment request failed: ${product.name}`, error);
                        toast({
                            title: t('product.frontend.deploy.requestFailure'),
                            description: t('product.frontend.deploy.requestFailureDescription', {
                                productName: product.name,
                            }),
                            variant: 'destructive',
                        });
                    }
                } catch (error) {
                    console.error(`Failed to process product ${product.name}:`, error);
                }
            }
        },
        [project, t, autoDeployMutation, queryClient, params]
    );

    const frontProducts = useMemo(() => {
        return query.data?.data?.filter(product => product.catalog$?.stereo === 'front') || [];
    }, [query.data?.data]);

    const frontProductsHash = useMemo(() => {
        return frontProducts
            .map(p => p.id)
            .sort()
            .join(',');
    }, [frontProducts]);

    // 배포 상태 변화 감지 로직
    useEffect(() => {
        if (!query.data?.data || !project) {
            return;
        }

        const { trackingProducts, productStatuses } = getDeploymentTrackingData();

        if (trackingProducts.length === 0) {
            setPreviousProductsStatus({});
            return;
        }

        const currentProducts = query.data.data;

        // 추적 중인 각 제품의 상태 변화 확인
        trackingProducts.forEach((trackingProductId: string) => {
            const currentProduct = currentProducts.find(p => p.id === trackingProductId);

            // localStorage에서 이전 상태 가져오기 (새로고침 대응)
            const savedPreviousStatus = getSavedProductStatus(trackingProductId);
            const memoryPreviousStatus = previousProductsStatus[trackingProductId];
            const previousStatus = memoryPreviousStatus || savedPreviousStatus;

            if (currentProduct) {
                // busy → running 상태 변화 감지
                if (previousStatus === 'busy' && currentProduct.status === 'running') {
                    toast({
                        title: t('product.frontend.deploy.success'),
                        description: t('product.frontend.deploy.successDescription', {
                            productName: currentProduct.name || '',
                        }),
                    });
                    // 배포 추적 종료
                    stopDeploymentTracking(trackingProductId);
                } else {
                    // 현재 상태를 localStorage에 업데이트
                    if (productStatuses[trackingProductId] !== currentProduct.status) {
                        updateDeploymentStatus(trackingProductId, currentProduct.status || '');
                    }
                }
            } else {
                stopDeploymentTracking(trackingProductId);
            }
        });

        // 현재 추적 중인 제품들의 상태를 메모리에 저장
        const { trackingProducts: updatedTrackingProducts } = getDeploymentTrackingData();
        const newStatus: Record<string, string> = {};
        updatedTrackingProducts.forEach((trackingProductId: string) => {
            const currentProduct = currentProducts.find(p => p.id === trackingProductId);
            if (currentProduct) {
                newStatus[trackingProductId] = currentProduct.status || '';
            }
        });

        setPreviousProductsStatus(newStatus);
    }, [query.data, project, t]);

    useEffect(() => {
        if (!frontProducts.length || !project || !enableToastAndDeploy) {
            return;
        }

        if (isProcessingRunning.current) {
            return;
        }

        isProcessingRunning.current = true;
        processProducts(frontProducts).finally(() => {
            isProcessingRunning.current = false;
        });
    }, [frontProductsHash, project, enableToastAndDeploy, processProducts]);

    return query;
};

export const useFetchProducts = (params: Params) =>
    useQuery<PaginationType<ProductView[]>>({
        queryKey: productsKey.list({ ...params }),
        queryFn: async () => {
            const result = await fetchProducts(params);
            return { ...result, data: result.list || [] } as PaginationType<ProductView[]>;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: 0,
    });

export const useFetchProductById = (productId?: string) => {
    return useQuery<ProductView>({
        queryKey: productsKey.detail(productId as string),
        queryFn: () => fetchProductById(productId),
        refetchOnWindowFocus: false,
        enabled: !!productId,
        refetchOnMount: true,
        staleTime: 0,
    });
};

export const useProductPolling = (productId?: string) => {
    return useQuery<ProductViewWithLogEvents>({
        queryKey: productsKey.detail(productId as string),
        queryFn: () => fetchProductById(productId),
        refetchOnWindowFocus: false,
        enabled: !!productId,
        refetchOnMount: true,
        staleTime: 0,
        refetchInterval: 1000 * 5, // 5초마다 갱신,
    });
};

export const useDeleteProduct = () => {
    return useCustomMutation<ProductView, AxiosError<string>, string>((productId: string) => deleteProduct(productId), {
        onError: error => {
            toast({ title: `${error.response?.data}` });
        },
    });
};

export const useApproveProduct = () => {
    return useCustomMutation((dto: ApproveProductDTO) => approveProduct(dto), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useCancelProduct = () => {
    return useCustomMutation((productId: string) => cancelProduct(productId), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useAutoDeployProduct = () => {
    const [currentProductId, setCurrentProductId] = useState<string | null>(null);

    const mutation = useCustomMutation(
        async (productId: string) => {
            setCurrentProductId(productId); // 처리 중인 ID 저장
            const response = await autoDeployProduct({ productId });
            return response;
        },
        {
            onError: error => {
                toast({
                    title: error instanceof Error ? error.message : 'An unknown error occurred',
                });
            },
            onSettled: () => {
                setCurrentProductId(null); // 처리 완료 후 초기화
            },
        }
    );

    return {
        ...mutation,
        currentProductId,
    };
};

export const useProductSettingsById = (productId?: string) => {
    return useQuery<SettingSetView[]>({
        queryKey: productSettingsKey.detail(productId as string),
        queryFn: () => fetchProductSettingsById(productId),
        refetchOnWindowFocus: false,
        enabled: !!productId,
        refetchOnMount: true,
        staleTime: 0,
    });
};

export const useUpdateProductSettings = () => {
    return useCustomMutation((dto: UpdateProductSettingsDTO) => updateProductSettings(dto), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useFetchChildProducts = (parentProductId?: string) =>
    useQuery<PaginationType<ProductView[]>>({
        queryKey: productsKey.children(parentProductId),
        queryFn: async () => {
            if (!parentProductId) {
                throw new Error('parentProductId is required');
            }
            const result = await fetchChildProducts(parentProductId);
            return { ...result, data: result.list || [] } as PaginationType<ProductView[]>;
        },
        enabled: !!parentProductId,
        refetchOnMount: true,
        staleTime: 0,
        refetchInterval: 10000,
    });
